import { fm } from '@/utils/i18nText';
// 权限控制函数
// 关键修复 2026-07-19: 修复 VIP 登录后可见所有菜单的严重越权漏洞
// 原因: 阶段2(已登录但permissions为空)触发 allTrue() 兜底, 导致所有菜单显示
// 修复: 阶段2不再 allTrue, 改为严格权限检查; permissions为空时根据roles判断
// 安全保障: 前端access只控制菜单显示, 真正权限控制由后端PermissionHandle中间件执行

function allTrue() {
  // 仅在 initialState 未加载完(currentUser 不存在)时使用
  // 此时用户未认证, 后端会拒绝所有请求, 前端 allTrue 不影响安全
  return {
    canAdmin: true,
    canVIP: true,
    canRole: () => true,
    canPermission: () => true,
    canAdminManage: () => true,
    canDashboard: () => true,
    canCompany: () => true,
    canCompanyAdmin: () => true,
    canReferrerEdit: () => true,
    canNavigation: () => true,
    canArticleCategory: () => true,
    canSeo: () => true,
    canArticle: () => true,
    canManualWriting: () => true,
    canAiWriting: () => true,
    canBannedWord: () => true,
    canSmsRecord: () => true,
    canEngine: () => true,
    canAiArticleCost: () => true,
    canCostRecord: () => true,
    canSiteConfig: () => true,
    canCompanyConfig: () => true,
    canRegionalSite: () => true,
    canRegionalSiteAny: () => true,
    canProvince: () => true,
    canCity: () => true,
    canDistrict: () => true,
    canWithdrawal: () => true,
    canTransactions: () => true,
    canRecharge: () => hasPermission(fm('充值管理')),
    canContacts: () => hasPermission('通讯录'),
    canCrmStudio: () => hasPermission('客户系统'),
    canNaturalPerson: () => hasPermission('自然人管理'),
    canNaturalPersonEdit: () => hasPermission('自然人编辑'),
    canNaturalPersonVerify: () => hasPermission('自然人复核'),
    canSearch: () => true,
    canGlobalPayment: () => true,
    canMemberDisplay: () => true,
    canCeosAssistant: () => true,
    canCompliance: () => true,
  };
}

// 全部拒绝 - 异常状态(角色未分配权限)时使用
function allFalse() {
  return {
    canAdmin: false,
    canVIP: false,
    canRole: () => false,
    canPermission: () => false,
    canAdminManage: () => false,
    canDashboard: () => false,
    canCompany: () => false,
    canCompanyAdmin: () => false,
    canReferrerEdit: () => false,
    canNavigation: () => false,
    canArticleCategory: () => false,
    canSeo: () => false,
    canArticle: () => false,
    canManualWriting: () => false,
    canAiWriting: () => false,
    canBannedWord: () => false,
    canSmsRecord: () => false,
    canEngine: () => false,
    canAiArticleCost: () => false,
    canCostRecord: () => false,
    canSiteConfig: () => false,
    canCompanyConfig: () => false,
    canRegionalSite: () => false,
    canRegionalSiteAny: () => false,
    canProvince: () => false,
    canCity: () => false,
    canDistrict: () => false,
    canWithdrawal: () => false,
    canTransactions: () => false,
    canRecharge: () => hasPermission(fm('充值管理')),
    canContacts: () => hasPermission('通讯录'),
    canCrmStudio: () => hasPermission('客户系统'),
    canNaturalPerson: () => hasPermission('自然人管理'),
    canNaturalPersonEdit: () => hasPermission('自然人编辑'),
    canNaturalPersonVerify: () => hasPermission('自然人复核'),
    canSearch: () => false,
    canGlobalPayment: () => false,
    canMemberDisplay: () => false,
    canCeosAssistant: () => false,
    canCompliance: () => false,
  };
}

export default function access(initialState: { currentUser?: API.CurrentUser; permissions?: string[]; roles?: string[] }) {
  const roles = initialState?.roles || [];
  const permissions = initialState?.permissions || [];
  const currentUser = initialState?.currentUser;
  const SUPER = '\u8d85\u7ea7\u7ba1\u7406\u5458';
  const isSuperAdmin = roles.includes(SUPER);

  // 阶段1: initialState 未加载完（没有 currentUser）→ 全部 true
  // 原因: UmiJS access 在 getInitialState 完成前就会执行, 此时未认证
  // 后端 auth:admin 中间件会拒绝所有未认证请求, 前端 allTrue 不影响安全
  if (!currentUser) {
    return allTrue();
  }

  // 阶段2: 已登录 - 严格权限检查
  // 如果 roles 和 permissions 都为空 → 异常状态(角色未分配权限) → 全部 false
  // 修复前: 此处返回 allTrue() 导致 VIP 等无权限角色看到所有菜单 (严重越权)
  if (roles.length === 0 && permissions.length === 0) {
    return allFalse();
  }

  // 阶段3: 正常权限检查
  const hasPermission = (permName: string): boolean => {
    if (isSuperAdmin) return true;
    return permissions.includes(permName);
  };

  const isVIP = roles.includes('VIP\u4f01\u4e1a');

  return {
    canAdmin: isSuperAdmin,
    canVIP: isVIP,
    canRole: () => isSuperAdmin,
    canPermission: () => isSuperAdmin,
    // 2026-09-20: 原写死 isSuperAdmin, 导致持有'管理员管理'权限的角色(省域中心主任)看不到该菜单
    canAdminManage: () => hasPermission('\u7ba1\u7406\u5458\u7ba1\u7406'),
    canDashboard: () => hasPermission('\u63a7\u5236\u53f0'),
    canCompany: () => hasPermission('\u4f01\u4e1a\u7ba1\u7406'),
    canCompanyAdmin: () => hasPermission('\u4f01\u4e1a\u7ba1\u7406\u5458'),
    // 2026-10-15: 推荐关系（推荐企业）只允许「超级管理员」或持有「推荐关系修正」指定权限的人改动；
    //   其余人编辑企业时该字段只读、默认保留已绑定的推荐人（后端 update 也有同样闸做兜底）。
    canReferrerEdit: () => isSuperAdmin || hasPermission('\u63a8\u8350\u5173\u7cfb\u4fee\u6b63'),
    canNavigation: () => hasPermission('\u5bfc\u822a\u7ba1\u7406'),
    canArticleCategory: () => hasPermission('\u6587\u7ae0\u5206\u7c7b'),
    canSeo: () => hasPermission('SEO\u7ba1\u7406'),
    canArticle: () => hasPermission('\u6587\u7ae0\u7ba1\u7406'),
    canManualWriting: () => hasPermission('\u624b\u5de5\u5199\u4f5c'),
    canAiWriting: () => hasPermission('AI\u667a\u80fd\u5199\u4f5c'),
    canBannedWord: () => hasPermission('\u654f\u611f\u8bcd\u7ba1\u7406'),
    canSmsRecord: () => hasPermission('\u77ed\u4fe1\u8bb0\u5f55\u7ba1\u7406'),
    canEngine: () => hasPermission(fm('引擎管理')),
    canAiArticleCost: () => hasPermission('AI\u6587\u7ae0\u6210\u672c\u8bb0\u5f55'),
    canCostRecord: () => hasPermission('\u6210\u672c\u8bb0\u5f55'),
    // 2026-09-18 修正: 原写成「企业网站配置」, 与权限表 name「企庭中国民营经济网配置」不匹配, 导致非超管永远看不到该菜单
    canSiteConfig: () => hasPermission('\u4f01\u5ead\u4e2d\u56fd\u6c11\u8425\u7ecf\u6d4e\u7f51\u914d\u7f6e'),
    canCompanyConfig: () => hasPermission('\u516c\u53f8\u914d\u7f6e\u7ba1\u7406'),
    canRegionalSite: () => hasPermission('\u57ce\u5e02\u5206\u7ad9'),
    // 2026-09-20: 父菜单任一子权限即可见(城市中心主任有城市/区域管理, 原被父级 access 挡掉整块不可达)
    canRegionalSiteAny: () => hasPermission('\u57ce\u5e02\u5206\u7ad9') ||
      hasPermission('\u7701\u4efd\u7ba1\u7406') || hasPermission('\u57ce\u5e02\u7ba1\u7406') ||
      hasPermission('\u533a\u57df\u7ba1\u7406'),
    canProvince: () => hasPermission('\u7701\u4efd\u7ba1\u7406'),
    canCity: () => hasPermission('\u57ce\u5e02\u7ba1\u7406'),
    canDistrict: () => hasPermission('\u533a\u57df\u7ba1\u7406'),
    canWithdrawal: () => hasPermission('\u63d0\u73b0\u5ba1\u6838'),
    canTransactions: () => hasPermission('\u4ea4\u6613\u8bb0\u5f55'),
    canRecharge: () => hasPermission(fm('充值管理')),
    canContacts: () => hasPermission('通讯录'),
    canCrmStudio: () => hasPermission('客户系统'),
    canNaturalPerson: () => hasPermission('自然人管理'),
    canNaturalPersonEdit: () => hasPermission('自然人编辑'),
    canNaturalPersonVerify: () => hasPermission('自然人复核'),
    canSearch: () => hasPermission('\u5168\u7ad9\u641c\u7d22'),
    canGlobalPayment: () => hasPermission('\u5168\u7403\u652f\u4ed8'),
    canMemberDisplay: () => hasPermission('\u4f1a\u5458\u5c55\u793a'),
    canCeosAssistant: () => hasPermission('\u603b\u88c1\u52a9\u7406'),
    canCompliance: () => hasPermission(fm('制裁筛查复核')),
  };
}
